// src/pages/AdminPanel/ProductList.jsx
import React, { useEffect, useState } from 'react';
import { db } from '../../services/firebase';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { Link } from 'react-router-dom'; // Using Link for navigation to Add/Edit
import Swal from 'sweetalert2'; // For confirmation dialogs
import { PlusCircle, Edit, Trash2, Settings, Gift, BarChart, Wallet2, PackageCheck } from 'lucide-react'; // Icons
import "./ProductList.css";

const ProductList = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      const querySnapshot = await getDocs(collection(db, 'products'));
      const productsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setProducts(productsData);
    } catch (err) {
      console.error("Error fetching products:", err);
      setError("Failed to load products. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleDelete = async (productId, productName) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete "${productName}". This action cannot be undone!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      background: '#1a202c', // Dark background for Swal
      color: '#e2e8f0', // Light text for Swal
      customClass: {
        popup: 'swal2-popup-custom',
        title: 'swal2-title-custom',
        htmlContainer: 'swal2-html-container-custom'
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteDoc(doc(db, 'products', productId));
          Swal.fire({
            title: 'Deleted!',
            text: 'Your product has been deleted.',
            icon: 'success',
            background: '#1a202c',
            color: '#e2e8f0',
            customClass: { popup: 'swal2-popup-custom', title: 'swal2-title-custom' }
          });
          fetchProducts(); // Refresh the list
        } catch (err) {
          console.error("Error deleting product:", err);
          Swal.fire({
            title: 'Error!',
            text: `Failed to delete product: ${err.message}`,
            icon: 'error',
            background: '#1a202c',
            color: '#e2e8f0',
            customClass: { popup: 'swal2-popup-custom', title: 'swal2-title-custom' }
          });
        }
      }
    });
  };

  if (loading) return <div className="text-center py-10 text-gray-300 text-xl">Loading Products...</div>;
  if (error) return <div className="text-center py-10 text-red-400 text-xl">{error}</div>;

  return (
    <div className="product-list-container-main"> {/* Main container for page-level styling */}
      <div className="product-list-header-section">
        <h2 className="section-title">Product Management</h2> {/* Consistent title styling */}
        <Link 
          to="/admin/products/add" 
          className="add-product-button"
        >
          <PlusCircle className="mr-2" size={20} /> Add New Product
        </Link>
      </div>

      {products.length === 0 ? (
        <div className="empty-state-message">No products found. Start by adding one!</div>
      ) : (
        <div className="product-table-wrapper"> {/* Container for the table for shadow/border */}
          <table className="product-table"> {/* The actual HTML table */}
            <thead>
              <tr className="table-header-row">
                <th className="px-6 py-3 text-left">Image</th>
                <th className="px-6 py-3 text-left">Name</th>
                <th className="px-6 py-3 text-left">Category</th>
                <th className="px-6 py-3 text-left">Price</th>
                <th className="px-6 py-3 text-left">Stock</th>
                <th className="px-6 py-3 text-left">Status</th>
                <th className="px-6 py-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map(product => (
                <tr className="table-row" key={product.id}>
                  <td className="px-6 py-4">
                    <img 
                      src={product.imageUrl || 'https://placehold.co/50x50/333/fff?text=No+Img'} 
                      alt={product.name} 
                      className="product-thumbnail" 
                      onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/50x50/333/fff?text=No+Img'; }} // Fallback image
                    />
                  </td>
                  <td className="px-6 py-4 product-name-cell">{product.name}</td>
                  <td className="px-6 py-4">{product.category}</td>
                  <td className="px-6 py-4">${(product.price / 100).toFixed(2)}</td> {/* Ensure price is converted from cents */}
                  <td className="px-6 py-4">{product.stock}</td>
                  <td className="px-6 py-4">
                    <span className={`status-badge ${product.isActive ? 'status-active' : 'status-inactive'}`}>
                      {product.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 action-buttons-cell">
                    <Link 
                      to={`/admin/products/edit/${product.id}`} 
                      className="edit-button"
                    >
                      <Edit size={16} className="mr-1" /> Edit
                    </Link>
                    <button 
                      onClick={() => handleDelete(product.id, product.name)} 
                      className="delete-button"
                    >
                      <Trash2 size={16} className="mr-1" /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <section className="dashboard-quick-actions">
        <h2 className="section-title">Quick Actions</h2>
        <div className="actions-grid">
          <Link 
            to="/admin/products/add" 
            className="action-button primary"
          >
            <PlusCircle className="mr-2" size={24} /> Add New Product
          </Link>
          <Link 
            to="/admin/settings"
            className="action-button secondary"
          >
            <Settings className="mr-2" size={24} /> Manage Settings
          </Link>
          <button 
            className="action-button secondary"
            onClick={() => alert('Manage Discount Codes functionality will be built later!')}
          >
            <Gift className="mr-2" size={24} /> Manage Discount Codes
          </button>
          <Link 
            to="/admin/analytics"
            className="action-button secondary"
          >
            <BarChart className="mr-2" size={24} /> View Analytics
          </Link>
          <Link 
            to="/admin/wallet"
            className="action-button secondary"
          >
            <Wallet2 className="mr-2" size={24} /> My Wallet
          </Link>
          <Link 
            to="/admin/shipment-center"
            className="action-button secondary"
          >
            <PackageCheck className="mr-2" size={24} /> Shipment Center
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ProductList;
